import React, { useState, useCallback, useEffect, useContext } from 'react';
import _ from 'lodash';
import axios from 'axios';
import { Redirect, useHistory } from 'react-router';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import HeightIcon from '@material-ui/icons/Height';
import CancelIcon from '@material-ui/icons/Cancel';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import Divider from '@material-ui/core/Divider';
import type { CalendarArgs } from '@this/domain/appoint/calendar';
import Calendar from '@this/domain/appoint/calendar';
import AppointContext from '@this/components/appoint/appoint_context';
import Utils from '@this/domain/appoint/utils';
import { Section, ButtonProgress } from './common';

interface Response {
  calendars: CalendarArgs[];
}

interface SlackTokenResponse {
  connected: boolean;
}

interface Props {
  onClose: () => void;
}

const DrawerServiceMenu = observer(({ onClose }: Props) => {
  const contextValue = useContext(AppointContext);
  const history = useHistory();
  const user = contextValue.user;
  const env = contextValue.env;
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [isCalendarsLoading, setIsCalendarsLoading] = useState<boolean>(false);
  const [isDefaultChanging, setIsDefaultChanging] = useState<boolean>(false);
  const [isSlackTokenLoading, setIsSlackTokenLoading] = useState<boolean>(false);
  const [isSlackConnected, setIsSlackConnected] = useState<boolean>(false);
  const [redirect, setRedirect] = useState<string | null>(null);

  const fetchCalendars = useCallback(async () => {
    setIsCalendarsLoading(true);
    const res = await axios.get<Response>('/aicalendar/calendars.json');
    const cals = res.data.calendars.map(c => new Calendar(c));
    setCalendars(cals);
    setIsCalendarsLoading(false);
    setIsDefaultChanging(false);
  }, [calendars]);

  const fetchSlackStatus = useCallback(async () => {
    setIsSlackTokenLoading(true);
    const res = await axios.get<SlackTokenResponse>('/aicalendar/slack/authorization.json');
    setIsSlackConnected(res.data.connected);
    setIsSlackTokenLoading(false);
  }, []);

  // マウント時に1回発動
  useEffect(() => {
    fetchCalendars();
    fetchSlackStatus();
    Utils.trackEvent('[cal][view] calendars');
  }, []);

  const handleDefaultCalendarChange = async (id: string) => {
    setIsDefaultChanging(true);
    await axios.put(`/aicalendar/auth/${id}`, {});
    fetchCalendars();
  };

  const handleCalendarDisconnect = async (id: string) => {
    await axios.delete(`/aicalendar/auth/${id}`, {});
    location.reload();
  };

  const handleZoomDisconnect = async () => {
    await axios.delete('/aicalendar/zoom/authorization', {});
    setRedirect('/');
  };

  const handleSlackDisconnect = async () => {
    await axios.delete('/aicalendar/slack/authorization', {});
    setRedirect('/');
  };

  return (
    <>
      {redirect && <Redirect to={redirect} />}
      <Header>
        {contextValue.isMobile ? (
          <Button
            variant="text"
            onClick={onClose}
            style={{ marginLeft: '-16px', fontSize: 11, fontWeight: 'bold' }}
          >
            <KeyboardArrowLeftIcon fontSize="small" />
            戻る
          </Button>
        ) : (
          <Title>サービス連携</Title>
        )}
        <CloseButton onClick={onClose} />
      </Header>
      <TheContainer maxWidth="md">
        {contextValue.isMobile && <MobileTitle>サービス連携</MobileTitle>}
        <Section>
          <Box>
            <SectionTitle isMobile={contextValue.isMobile}>カレンダー</SectionTitle>
            <Box>
              <SyncButton
                variant="contained"
                color="primary"
                isMobile={contextValue.isMobile}
                startIcon={<SyncIcon fontSize="small" />}
                onClick={() => {
                  window.open(history.createHref({ pathname: '/auth/new?provider=outlook' }));
                }}
              >
                Outlook Calendarと連携
              </SyncButton>
            </Box>
            <Box>
              <SyncButton
                variant="contained"
                color="primary"
                isMobile={contextValue.isMobile}
                startIcon={<SyncIcon fontSize="small" />}
                onClick={() => {
                  window.open(history.createHref({ pathname: '/auth/new?provider=google' }));
                }}
              >
                Google Calendarと連携
              </SyncButton>
            </Box>
          </Box>
          <CalendarListWrapper style={{ position: 'relative' }}>
            {isCalendarsLoading && <ButtonProgress />}
            <RadioGroup>
              <List>
                {calendars.map(c => (
                  <ListItem key={c.id}>
                    <Box>
                      <TheRadio
                        color="primary"
                        checked={c.isDefault}
                        disabled={isDefaultChanging}
                        onChange={e => handleDefaultCalendarChange(e.target.value)}
                        value={c.authDataId}
                      />
                    </Box>
                    <Box marginLeft="10px" marginRight="auto">
                      <CalendarName>
                        {c.name}
                        {c.isDefault && '（デフォルト）'}
                      </CalendarName>
                      <CalendarEmail>
                        {c.email && (
                          <>
                            <MailOutlineIcon fontSize="small" />
                            <span>{c.email}</span>
                          </>
                        )}
                      </CalendarEmail>
                    </Box>
                    <Box>
                      <Button
                        size="small"
                        color="primary"
                        variant="text"
                        onClick={() => handleCalendarDisconnect(c.authDataId)}
                        style={{ padding: '0', marginTop: '5px', textTransform: 'none', display: 'blcok' }}
                      >
                        連携を解除
                      </Button>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </RadioGroup>
          </CalendarListWrapper>
        </Section>
        <TheDivider />
        <Section>
          <Box>
            <SectionTitle isMobile={contextValue.isMobile}>オンラインミーティング</SectionTitle>
            <List>
              <ListItem>
                <ServiceName isMobile={contextValue.isMobile}>Zoom</ServiceName>
                {user!.appointZoomAccessToken ? (
                  <Button
                    size="small"
                    color="primary"
                    variant="text"
                    onClick={() => handleZoomDisconnect()}
                    style={{ padding: '0', marginTop: '5px', textTransform: 'none', display: 'blcok' }}
                  >
                    連携を解除
                  </Button>
                ) : (
                  <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      window.open(history.createHref({ pathname: '/zoom/authorization/new' }));
                    }}
                  >
                    連携
                  </Button>
                )}
              </ListItem>
            </List>
          </Box>
        </Section>
        {env !== 'production' && user && (
          <>
            <TheDivider />
            <Section>
              <Box>
                <SectionTitle isMobile={contextValue.isMobile}>チャットサービス</SectionTitle>
                <List>
                  <ListItem>
                    <ServiceName isMobile={contextValue.isMobile}>Slack</ServiceName>
                    <Box marginLeft="auto" position="relative">
                      {isSlackTokenLoading ? (
                        <ButtonProgress />
                      ) : isSlackConnected ? (
                        <Button
                          size="small"
                          color="primary"
                          variant="text"
                          onClick={() => handleSlackDisconnect()}
                          style={{ padding: '0', marginTop: '5px', textTransform: 'none', display: 'blcok' }}
                        >
                          連携を解除
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          onClick={() => {
                            window.open(history.createHref({ pathname: '/slack/authorization/new' }));
                          }}
                        >
                          連携
                        </Button>
                      )}
                    </Box>
                  </ListItem>
                </List>
              </Box>
            </Section>
          </>
        )}
      </TheContainer>
    </>
  );
});

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${props => props.theme.grayBgColorLight};
  padding: 16px;
`;

const Title = styled(Typography)`
  margin-bottom: 0;
  font-size: 20px;
  font-weight: bold;
`;

const MobileTitle = styled(Title)`
  margin-bottom: 10px;
  font-size: 16px;
`;

const CloseButton = styled(CancelIcon)`
  display: block;
  color: #ddd;
  cursor: pointer;
`;

const TheContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 0 16px;
`;

const SectionTitle = styled(Typography)<{ isMobile: boolean }>`
  margin: 0 0 15px 0;
  font-size: 16px;
  font-weight: bold;

  ${props =>
    props.isMobile &&
    `
    font-size: 12px;
  `}
`;

const SyncButton = styled(Button)<{ isMobile: boolean }>`
  margin-bottom: 10px;
  text-transform: none;

  ${props =>
    props.isMobile &&
    `
    width: 100%;
  `}
`;

const CalendarListWrapper = styled(Box)`
  margin-top: 10px;
`;

const TheRadio = styled(Radio)`
  padding: 0 5px 0 0;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
`;

const ListItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
`;

const CalendarName = styled(Typography)`
  font-weight: bold;
  font-size: 0.8em;
`;

const CalendarEmail = styled(Typography)`
  display: flex;
  align-items: center;
  font-size: 0.8em;
  color: ${props => props.theme.grayTextColor};

  & > svg {
    margin-right: 5px;
  }
`;

const TheDivider = styled(Divider)`
  margin: 20px 0;
`;

const SyncIcon = styled(HeightIcon)`
  transform: rotate(90deg);
`;

const ServiceName = styled(Typography)<{ isMobile: boolean }>`
  font-size: 16px;
  font-weight: bold;

  ${props =>
    props.isMobile &&
    `
    font-size: 12px;
  `}
`;

export default DrawerServiceMenu;
